/**
 * Samples carousel in the cart drawer (`.samples-slider`).
 * Loads after `swiper.bundle.js` (see script-assets.liquid).
 */

import { SWIPER_DRAG_DEFAULTS } from './swiper-feel.js';
import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';
import { Component, defineComponent } from './component-base.js';
import { updateCartBubble } from './utilities.js';

/** @type {import('swiper').Swiper | null} */
let samplesSwiper = null;

function destroySamplesSlider() {
  if (samplesSwiper) {
    try {
      samplesSwiper.destroy(true, true);
    } catch {
      /* noop */
    }
    samplesSwiper = null;
  }
}

function initSamplesSlider() {
  if (typeof window.Swiper !== 'function') return;

  const el = document.querySelector('.samples-slider');
  if (!el) return;

  const slides = el.querySelectorAll('.swiper-slide');
  if (slides.length === 0) return;

  destroySamplesSlider();

  samplesSwiper = new window.Swiper(el, {
    ...SWIPER_DRAG_DEFAULTS,
    slidesPerView: 2.5,
    spaceBetween: 15,
    watchOverflow: true,
    mousewheel: true,
    breakpoints: {
      768: {
        slidesPerView: 3.3,
        spaceBetween: 25,
      },
    },
  });
}

function scheduleInit() {
  requestAnimationFrame(() => initSamplesSlider());
}

function scrollCartDrawerToTop() {
  document.querySelector('.cart-drawer-body')?.scrollTo({ top: 0, behavior: 'smooth' });
}

/** Scroll list region after cart refresh (DOM may swap mid-frame). */
function scrollCartDrawerToTopSoon() {
  scrollCartDrawerToTop();
  requestAnimationFrame(() => requestAnimationFrame(() => scrollCartDrawerToTop()));
  window.setTimeout(scrollCartDrawerToTop, 120);
}

let samplesSliderObserverDebounce = 0;
function observeCartDrawerForSamplesSlider() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || drawer.dataset.samplesSliderMo === '1') return;
  drawer.dataset.samplesSliderMo = '1';
  const mo = new MutationObserver(() => {
    clearTimeout(samplesSliderObserverDebounce);
    samplesSliderObserverDebounce = window.setTimeout(() => scheduleInit(), 0);
  });
  mo.observe(drawer, { childList: true, subtree: true });
}

function bootSamplesSlider() {
  observeCartDrawerForSamplesSlider();
  scheduleInit();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSamplesSlider);
} else {
  bootSamplesSlider();
}

ThemeBus.addEventListener(EVENTS.cartDrawerOpen, () => {
  observeCartDrawerForSamplesSlider();
  scheduleInit();
});

window.addEventListener('pageshow', (e) => {
  if (e.persisted) scheduleInit();
});

// --- Cart sample products (checkbox add/remove) ---

async function fetchCartJson() {
  const res = await fetch('/cart.js');
  if (!res.ok) throw new Error('Cart fetch failed');
  return res.json();
}

/**
 * Variant IDs for all sample checkboxes under a `cart-samples` root (for max-sample counting).
 * @param {ParentNode} root
 */
function getSampleVariantIds(root) {
  const ids = new Set();
  root.querySelectorAll('[data-sample-add-to-cart-checkbox][data-variant-id]').forEach((el) => {
    const id = parseInt(el.getAttribute('data-variant-id') || '', 10);
    if (id) ids.add(id);
  });
  return ids;
}

/**
 * Total quantity of cart lines whose variant is one of the sample products in the drawer.
 */
function countSampleQuantityInCart(cart, sampleVariantIds) {
  if (!cart?.items) return 0;
  let qty = 0;
  for (const item of cart.items) {
    if (sampleVariantIds.has(Number(item.variant_id))) qty += item.quantity;
  }
  return qty;
}

export class CartSampleProductComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.initialized) return;
    this.initialized = true;

    this.checkbox = this.querySelector('[data-sample-add-to-cart-checkbox]');
    if (!this.checkbox) return;

    this.checkbox.addEventListener('change', this.#onCheckboxChange);
    try {
      ThemeBus.addEventListener(EVENTS.cartUpdated, this.#syncCheckboxFromCart);
    } catch (_) {}
    this.#syncCheckboxFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.checkbox?.removeEventListener('change', this.#onCheckboxChange);
    try {
      ThemeBus.removeEventListener(EVENTS.cartUpdated, this.#syncCheckboxFromCart);
    } catch (_) {}
  }

  #syncCheckboxFromCart = async () => {
    if (!this.checkbox) return;
    const variantId = parseInt(this.checkbox.getAttribute('data-variant-id') || '', 10);
    if (!variantId) return;
    try {
      const cart = await fetchCartJson();
      const line = cart.items?.find((item) => Number(item.variant_id) === variantId);
      this.checkbox.checked = (line?.quantity ?? 0) > 0;
      const root = this.closest('cart-samples') || document.querySelector('cart-samples');
        const max = root ? parseInt(root.getAttribute('data-max-samples') || '0', 10) : 0;
        const ids = root ? getSampleVariantIds(root) : new Set();
        if (root && max > 0) {
        root.classList.toggle('samples-at-max', countSampleQuantityInCart(cart, ids) >= max);
}
    } catch (e) {
      console.warn('[CartSampleProduct] sync failed', e);
    }
  };

  #onCheckboxChange = async (event) => {
    const checkbox = /** @type {HTMLInputElement} */ (event.target);
    const variantId = parseInt(checkbox.getAttribute('data-variant-id') || '', 10);
    if (!variantId) return;

    const cartSamplesRoot = this.closest('cart-samples') || document.querySelector('cart-samples');
    const maxSamples = cartSamplesRoot
      ? parseInt(cartSamplesRoot.getAttribute('data-max-samples') || '0', 10)
      : 0;
    const sampleIds = getSampleVariantIds(cartSamplesRoot || document);

    if (checkbox.checked) {
      try {
        let cart = await fetchCartJson();
        const already = cart.items?.find((item) => Number(item.variant_id) === variantId);
        if (already && already.quantity > 0) {
          checkbox.checked = true;
          return;
        }

        const currentSampleQty = countSampleQuantityInCart(cart, sampleIds);
        if (maxSamples > 0 && currentSampleQty >= maxSamples) {
            if (cartSamplesRoot) cartSamplesRoot.classList.add('samples-at-max');
          checkbox.checked = false;
          return;
        }
        checkbox.disabled = true;
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const result = await response.json();

        if (result.status && result.status !== 200) {
          checkbox.checked = false;
          checkbox.disabled = false;
          return;
        }

        cart = await fetchCartJson();
        if (maxSamples > 0 && cartSamplesRoot && countSampleQuantityInCart(cart, sampleIds) >= maxSamples) {
            cartSamplesRoot.classList.add('samples-at-max');
          }
        window.__cart = cart;
        updateCartBubble();
        try {
          ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
          ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
          scrollCartDrawerToTopSoon();
        } catch (_) {}
      } catch (err) {
        console.error('[CartSampleProduct] add failed', err);
        checkbox.checked = false;
      } finally {
        checkbox.disabled = false;
      }
      return;
    }

    // Unchecked → remove line for this variant (quantity 0)
    try {
      checkbox.disabled = true;
      const cart = await fetchCartJson();
      const lineIndex = cart.items?.findIndex((item) => Number(item.variant_id) === variantId) ?? -1;
      if (lineIndex === -1) {
        checkbox.disabled = false;
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: lineIndex + 1, quantity: 0 }),
      });

      if (!response.ok) {
        checkbox.checked = true;
        checkbox.disabled = false;
        return;
      }

      const next = await fetchCartJson();
      if (cartSamplesRoot && (maxSamples === 0 || countSampleQuantityInCart(next, sampleIds) < maxSamples)) {
        cartSamplesRoot.classList.remove('samples-at-max');
      }
      window.__cart = next;
      updateCartBubble();
      try {
        ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
      } catch (_) {}
    } catch (err) {
      console.error('[CartSampleProduct] remove failed', err);
      checkbox.checked = true;
    } finally {
      checkbox.disabled = false;
    }
  };
}

function initCartSampleProduct() {
  defineComponent('cart-sample-product', CartSampleProductComponent);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartSampleProduct);
} else {
  initCartSampleProduct();
}